/**
 * Pure helpers for the manufacturer, brand and category pickers of the
 * product form (JD-423, JD-426).
 *
 * Each picker is an Autocomplete whose option list is built here from the
 * organization's rows, the typed text and the form's pending value:
 *   - rows are sorted by name (JD-426);
 *   - text that matches no row gets an `Add "text"` option, which selects a
 *     pending value: `{ pending: true, <name key>: text }`. Nothing is
 *     written until the product is saved; the API creates the rows then
 *     (manufacturerName / brandName / categoryName in the payload);
 *   - a brand typed under one manufacturer that already exists under another
 *     is offered from there (`foreignManufacturerName`) instead of being
 *     created twice, since the brand decides the manufacturer.
 */

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function trimmed(value) {
  return String(value ?? "").trim();
}

function sameName(a, b) {
  return trimmed(a).toLowerCase() === trimmed(b).toLowerCase();
}

function matches(name, text) {
  return !text || trimmed(name).toLowerCase().includes(text.toLowerCase());
}

/** A copy of `rows` sorted by `key`, case-insensitively, ignoring surrounding spaces. */
export function sortByName(rows, key) {
  return [...rows].sort((a, b) =>
    String(a?.[key] ?? "")
      .trim()
      .localeCompare(String(b?.[key] ?? "").trim(), undefined, {
        sensitivity: "base",
      })
  );
}

/**
 * Manufacturer names for the products table filter: the full picker list
 * plus any name already on a product. Same name ignoring case appears once
 * (the catalog spelling wins). Sorted A–Z ignoring case.
 */
export function manufacturerFilterOptions(catalogRows, productNames) {
  const byKey = new Map();
  const add = (raw) => {
    const name = String(raw ?? "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (!byKey.has(key)) byKey.set(key, name);
  };
  for (const row of catalogRows || []) add(row?.manufacturer_name);
  for (const raw of productNames || []) add(raw);
  return [...byKey.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

/** Label for an option or selected value of a picker keyed by `key`. */
export function optionLabel(option, key) {
  if (!option) return "";
  // freeSolo hands the raw text through while the user types.
  if (typeof option === "string") return option;
  const name = option[key] || "";
  if (option.inputValue) return `Add "${option.inputValue}"`;
  if (option.pending) return `${name} (new)`;
  if (option.foreignManufacturerName) {
    return `${name} (${option.foreignManufacturerName})`;
  }
  return name;
}

/**
 * `filtered` plus an add option for `inputValue`, unless the text is empty or
 * a row in `all` (or a pending row already in `filtered`) has that name.
 */
export function withAddOption(filtered, inputValue, { key, all }) {
  const text = trimmed(inputValue);
  if (!text) return filtered;
  const taken =
    all.some((row) => sameName(row[key], text)) ||
    filtered.some((row) => sameName(row[key], text));
  if (taken) return filtered;
  return [...filtered, { pending: true, inputValue: text, [key]: text }];
}

function choices(rows, key, text, pendingRow) {
  const list = sortByName(
    rows.filter((row) => matches(row[key], text)),
    key
  );
  return pendingRow && matches(pendingRow[key], text)
    ? [pendingRow, ...list]
    : list;
}

export function manufacturerChoices({
  manufacturers,
  inputValue,
  pendingManufacturer = null,
}) {
  const text = trimmed(inputValue);
  return withAddOption(
    choices(manufacturers, "manufacturer_name", text, pendingManufacturer),
    text,
    { key: "manufacturer_name", all: manufacturers }
  );
}

export function brandChoices({
  brands,
  manufacturers,
  manufacturerId,
  manufacturerIsPending = false,
  inputValue,
  pendingBrand = null,
}) {
  const text = trimmed(inputValue);
  const scoped = manufacturerIsPending || hasValue(manufacturerId);
  const own = manufacturerIsPending
    ? []
    : hasValue(manufacturerId)
      ? brands.filter((brand) => brand.manufacturer_id === manufacturerId)
      : brands;

  let list = choices(own, "brand_name", text, pendingBrand);

  if (scoped && text) {
    const foreign = brands
      .filter((brand) => !own.includes(brand) && sameName(brand.brand_name, text))
      .map((brand) => ({
        ...brand,
        foreignManufacturerName:
          manufacturers.find(
            (manufacturer) =>
              manufacturer.manufacturer_id === brand.manufacturer_id
          )?.manufacturer_name || "",
      }));
    list = [...list, ...foreign];
  }

  return withAddOption(list, text, { key: "brand_name", all: brands });
}

export function categoryChoices({
  categories,
  brandId,
  brandIsPending = false,
  inputValue,
  pendingCategory = null,
}) {
  const text = trimmed(inputValue);
  const own = brandIsPending
    ? []
    : hasValue(brandId)
      ? categories.filter((category) => category.brand_id === brandId)
      : categories;
  // Category names are only unique per brand, so a twin under another brand
  // does not block adding one here.
  return withAddOption(
    choices(own, "category_name", text, pendingCategory),
    text,
    { key: "category_name", all: own }
  );
}

/**
 * What a picker's onChange value means: `{ id, name, row }`. An existing row
 * gives its id (and the row); an add option, a pending value or free text
 * that matches no row gives a name to create; free text that matches a row,
 * ignoring case, gives that row. Nothing picked gives nulls.
 */
export function resolvePick(value, { idKey, nameKey, rows }) {
  if (value === null || value === undefined) {
    return { id: null, name: null, row: null };
  }
  if (typeof value === "string") {
    const text = trimmed(value);
    if (!text) return { id: null, name: null, row: null };
    const row = rows.find((candidate) => sameName(candidate[nameKey], text));
    return row
      ? { id: row[idKey], name: null, row }
      : { id: null, name: text, row: null };
  }
  if (value.inputValue || value.pending) {
    return { id: null, name: trimmed(value[nameKey]) || null, row: null };
  }
  return { id: value[idKey] ?? null, name: null, row: value };
}

/** The category part of the save payload: the id, or the pending name when there is none. */
export function categoryRefPayload(form) {
  const payload = {
    categoryId: hasValue(form.categoryId) ? form.categoryId : null,
  };
  if (payload.categoryId === null && trimmed(form.categoryName)) {
    payload.categoryName = trimmed(form.categoryName);
  }
  return payload;
}
